import axios from "axios"
import { ACCESS_TOKEN, REFRESH_TOKEN } from "./constants";
import { getApiUrl } from "./utils/apiConfig";

const apiUrl = getApiUrl();

const api = axios.create({
    baseURL: apiUrl,
    timeout: 600000, // 10 minutes (600 seconds) to match backend timeout for complex queries
});

// Flag to prevent multiple logout notifications
let isShowingLogoutNotification = false;

// Store notification callback
let notificationCallback = null;

// Function to set notification callback
export const setNotificationCallback = (callback) => {
  notificationCallback = callback;
};

api.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem(ACCESS_TOKEN);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

// Response interceptor to handle 401 errors globally
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Check if the error is a 401 (Unauthorized)
    if (error.response && error.response.status === 401) {
      handleLogout();
    }
    return Promise.reject(error);
  }
);

const handleLogout = () => {
  // Prevent multiple notifications
  if (isShowingLogoutNotification) {
    return;
  }
  
  isShowingLogoutNotification = true;
  
  // Clear tokens
  localStorage.removeItem(ACCESS_TOKEN);
  localStorage.removeItem(REFRESH_TOKEN);
  
  // Show logout notification using context if available, fallback to DOM method
  if (notificationCallback) {
    notificationCallback({
      type: 'warning',
      title: 'Session Expired',
      message: 'You have been logged out. Redirecting to login...',
      duration: 3000
    });
  } else {
    showLogoutNotification();
  }
  
  // Redirect to login after a short delay
  setTimeout(() => {
    window.location.href = '/login';
    isShowingLogoutNotification = false;
  }, 2000);
};

const showLogoutNotification = () => {
  // Create notification element
  const notification = document.createElement('div');
  notification.innerHTML = `
    <div style="
      position: fixed;
      top: 20px;
      right: 20px;
      background: linear-gradient(135deg, #ff6b6b, #ffa726);
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 10000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      max-width: 300px;
      animation: slideIn 0.3s ease-out;
    ">
      <div style="display: flex; align-items: center; gap: 8px;">
        <span style="font-size: 18px;">⚠️</span>
        <div>
          <div style="font-weight: 600; margin-bottom: 4px;">Session Expired</div>
          <div style="font-size: 12px; opacity: 0.9;">You have been logged out. Redirecting to login...</div>
        </div>
      </div>
    </div>
    <style>
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
    </style>
  `;
  
  document.body.appendChild(notification);
  
  // Remove notification after 3 seconds
  setTimeout(() => {
    if (notification && notification.parentNode) {
      notification.remove();
    }
    isShowingLogoutNotification = false;
  }, 3000);
};
  
  export default api;